import {
  ApplyPromoCodePage,
  AppDownloadPage,
  AuthPage,
  ChangeEmailPage,
  ChangePasswordPage,
  ChangeVoicePage,
  CheckoutPage,
  ContactUsPage,
  DeleteAccountPage,
  HomePage,
  LibraryDetailsPage,
  LibraryPage,
  LanguagePage,
  ManageSubscriptionPage,
  NotFoundPage,
  ProcessResetPasswordPage,
  AuthSessionsPage,
  ConnectEmailPage,
} from "@/pages"
import { ROUTER_PATHS } from "@/shared/constants"
import { Navigate, Route, Routes, useLocation } from "react-router-dom"
import { AuthRoute } from "./auth-route"
import { ProtectedRoute } from "./protected-route"
import { LayoutApp, LayoutAuth, LayoutProfile, LayoutUser } from "@/layouts"
import { useUserStore } from "@/entities"

const AccountOnlyRoute = ({ children }: { children: React.ReactNode }) => {
  const isGuest = useUserStore(state => state.isGuest)

  if (isGuest) {
    return <Navigate to={ROUTER_PATHS.PROFILE_CHANGE_VOICE} replace />
  }

  return children
}

export const RouterSetup = () => {
  const location = useLocation()
  return (
    <Routes location={location}>
      <Route
        path={ROUTER_PATHS.HOME}
        element={
          <AuthRoute>
            <HomePage />
          </AuthRoute>
        }
      />

      {/* USER ROUTES AND LAYOUT FOR RESET PASSWORD OR OTHER DIRECT LINKS  */}
      <Route path={ROUTER_PATHS.USER} element={<LayoutUser />}>
        <Route
          index
          element={<Navigate to={ROUTER_PATHS.USER_RESET_PASSWORD} replace />}
        />
        <Route
          path={ROUTER_PATHS.USER_RESET_PASSWORD}
          element={<ProcessResetPasswordPage />}
        />
      </Route>

      {/* AUTH ROUTES AND LAYOUT */}
      <Route
        path={ROUTER_PATHS.AUTH}
        element={
          <AuthRoute>
            <LayoutAuth />
          </AuthRoute>
        }
      >
        <Route index element={<AuthPage />} />
      </Route>

      {/* APP ROUTES AND LAYOUT */}
      <Route
        path={ROUTER_PATHS.APP}
        element={
          <ProtectedRoute>
            <LayoutApp />
          </ProtectedRoute>
        }
      >
        <Route
          index
          element={<Navigate to={ROUTER_PATHS.APP_LIBRARY} replace />}
        />
        <Route path={ROUTER_PATHS.APP_LIBRARY} element={<LibraryPage />} />
        <Route
          path={ROUTER_PATHS.APP_LIBRARY_DETAILS}
          element={<LibraryDetailsPage />}
        />
      </Route>

      {/* PROFILE ROUTES AND LAYOUT */}
      <Route
        path={ROUTER_PATHS.PROFILE}
        element={
          <ProtectedRoute>
            <LayoutProfile />
          </ProtectedRoute>
        }
      >
        <Route
          index
          element={<Navigate to={ROUTER_PATHS.PROFILE_CHANGE_VOICE} replace />}
        />

        <Route
          path={ROUTER_PATHS.PROFILE_CONNECT_EMAIL}
          element={<ConnectEmailPage />}
        />
        <Route
          path={ROUTER_PATHS.PROFILE_CHANGE_EMAIL}
          element={
            <AccountOnlyRoute>
              <ChangeEmailPage />
            </AccountOnlyRoute>
          }
        />
        <Route
          path={ROUTER_PATHS.PROFILE_APPLY_PROMO_CODE}
          element={<ApplyPromoCodePage />}
        />
        <Route
          path={ROUTER_PATHS.PROFILE_CHANGE_VOICE}
          element={<ChangeVoicePage />}
        />
        <Route
          path={ROUTER_PATHS.PROFILE_LANGUAGE}
          element={<LanguagePage />}
        />
        <Route
          path={ROUTER_PATHS.PROFILE_DELETE_ACCOUNT}
          element={<DeleteAccountPage />}
        />
        <Route
          path={ROUTER_PATHS.PROFILE_CHANGE_PASSWORD}
          element={
            <AccountOnlyRoute>
              <ChangePasswordPage />
            </AccountOnlyRoute>
          }
        />
        <Route
          path={ROUTER_PATHS.PROFILE_MANAGE_SUBSCRIPTION}
          element={
            <AccountOnlyRoute>
              <ManageSubscriptionPage />
            </AccountOnlyRoute>
          }
        />
        <Route
          path={ROUTER_PATHS.PROFILE_CONTACT_US}
          element={<ContactUsPage />}
        />
        <Route
          path={ROUTER_PATHS.PROFILE_AUTH_SESSIONS}
          element={<AuthSessionsPage />}
        />
      </Route>

      <Route
        path={ROUTER_PATHS.GUEST_UPGRADE}
        element={<Navigate to={ROUTER_PATHS.PROFILE_CONNECT_EMAIL} replace />}
      />

      {/* CHECKOUT */}
      <Route path={ROUTER_PATHS.CHECKOUT} element={<CheckoutPage />} />

      {/* APP DOWNLOAD */}
      <Route path={ROUTER_PATHS.DOWNLOAD_APP} element={<AppDownloadPage />} />

      {/* NOT FOUND ROUTE */}
      <Route path={ROUTER_PATHS.NOT_FOUND} element={<NotFoundPage />}></Route>

      {/* NOT FOUND ROUTE FOR ANY UNKNOWN ROUTE */}
      <Route
        path="*"
        element={<Navigate to={ROUTER_PATHS.NOT_FOUND} replace />}
      />
    </Routes>
  )
}
